import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const businessJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/business`,
    url: `${siteUrl}/business`,
    name: '事業内容｜特注ネジ製作・難削材加工・小ロット対応｜おかやまネジ製作所',
    description: '岡山市の精密ネジ製造業者おかやまネジ製作所の事業内容。特注ネジ製作・チタン/ステンレス難削材加工・1本からの小ロット対応。0.01mm精度の超精密加工。',
    inLanguage: 'ja',
    isPartOf: { '@id': `${siteUrl}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'トップ', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '事業内容', item: `${siteUrl}/business` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${siteUrl}/business/#service`,
    name: '精密ネジ製造・特注ネジ加工サービス',
    provider: {
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#business`,
      name: 'おかやまネジ製作所',
      url: siteUrl,
    },
    areaServed: [
      { '@type': 'City', name: '岡山市' },
      { '@type': 'City', name: '倉敷市' },
      { '@type': 'City', name: '総社市' },
    ],
    serviceType: '精密ネジ製造',
    description: '特注ネジ製作・難削材加工・小ロット対応。チタン・ステンレス・インコネルなど難削材の精密加工。1本から対応可能。',
    offers: {
      '@type': 'Offer',
      priceCurrency: 'JPY',
      availability: 'https://schema.org/InStock',
      description: '1本からの小ロット対応。お見積もり無料。',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'サービス一覧',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '特注ネジ製作', description: 'M1〜M30対応、0.01mm精度' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '難削材加工', description: 'チタン・ステンレス・インコネル・ハステロイ対応' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '小ロット・短納期対応', description: '1本から、最短1週間納期' } },
      ],
    },
  },
];

const BusinessPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = '事業内容｜特注ネジ製作・難削材加工・小ロット対応｜おかやまネジ製作所';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '岡山市の精密ネジ製造業者おかやまネジ製作所の事業内容。特注ネジ製作・チタン/ステンレス難削材加工・1本からの小ロット対応。0.01mm精度の超精密加工。');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${siteUrl}/business`);

    const scriptId = 'business-jsonld';
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(businessJsonLd);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  const services = [
    {
      icon: 'ri-settings-3-line',
      title: '特注ネジ製作',
      description: '規格品では対応できない特殊な形状のネジを、図面1枚から製作いたします。岡山市の製造業で培った50年の経験により、お客様のニーズに合わせた最適な提案が可能です。M1からM30まで幅広いサイズに対応し、0.01mm単位の超精密加工を実現します。試作品から量産まで、柔軟に対応いたします。',
    },
    {
      icon: 'ri-hammer-line',
      title: '難削材加工',
      description: 'チタン、ステンレス、インコネル、ハステロイなど、加工が難しい材質の精密加工を得意としています。航空宇宙、医療機器、化学プラントなど、高い品質が求められる分野での実績が豊富です。最新のCNC旋盤と熟練職人の技術により、難削材でも高精度な加工を実現します。',
    },
    {
      icon: 'ri-flashlight-line',
      title: '小ロット・短納期対応',
      description: '1本からの小ロット対応が可能です。新製品の試作開発や、緊急の補修部品製作など、お急ぎの案件にも柔軟に対応いたします。岡山市近隣であれば直接納品も可能で、設計担当者様との密なコミュニケーションにより、スピーディーな対応を実現します。',
    },
  ];

  const materials = [
    { name: 'チタン', features: '軽量・高強度・耐食性', applications: '航空宇宙・医療機器' },
    { name: 'ステンレス', features: '耐食性・耐熱性', applications: '食品機械・化学プラント' },
    { name: 'インコネル', features: '耐熱性・耐食性', applications: '航空機エンジン・原子力' },
    { name: '真鍮', features: '加工性・導電性', applications: '電気部品・装飾品' },
    { name: 'アルミニウム', features: '軽量・導電性', applications: '自動車・電子機器' },
    { name: 'ハステロイ', features: '耐食性・耐熱性', applications: '化学プラント・海洋機器' },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <PageHero
          title="事業内容"
          breadcrumbs={[
            { label: 'トップ', path: '/' },
            { label: '事業内容' },
          ]}
          imageUrl="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/ca283389bdc299f0218ee9a7a36d38d3.webp"
        />

        {/* 事業概要 */}
        <section className="py-16 sm:py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy mb-6 sm:mb-8">
                  私たちの事業
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-4">
                  おかやまネジ製作所は、創業50年の歴史を持つ岡山市の精密ネジ製造業者です。特注ネジの製作から難削材加工まで、幅広いニーズに対応しています。
                </p>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-4">
                  最新のCNC旋盤と熟練職人の技術を組み合わせることで、0.01mm単位の超精密加工を実現。規格品では対応できない特殊な形状のネジも、1本から製作可能です。
                </p>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  岡山市を中心とした近隣市町村の製造業の皆様に、高品質な製品と迅速なサービスを提供し続けています。
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/d4e84734291e79a9ece291c2163959e6.jpeg"
                  alt="職人による精密加工"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 品質管理 */}
        <section className="py-16 sm:py-20 lg:py-28 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-12 sm:mb-16">
              品質へのこだわり
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/0d7379e524ec93f9a7a7759d62ca5e80.jpeg"
                  alt="品質管理"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-navy mb-6 sm:mb-8">
                  徹底した品質管理体制
                </h3>
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-start">
                    <div className="w-10 h-10 flex items-center justify-center bg-navy rounded-full flex-shrink-0 mt-1">
                      <i className="ri-check-line text-xl text-white"></i>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg sm:text-xl font-bold text-navy mb-2">
                        全数検査
                      </h4>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        すべての製品について、三次元測定機による精密検査を実施しています。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 flex items-center justify-center bg-navy rounded-full flex-shrink-0 mt-1">
                      <i className="ri-check-line text-xl text-white"></i>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg sm:text-xl font-bold text-navy mb-2">
                        検査成績書の発行
                      </h4>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        ご要望に応じて、測定データを記載した検査成績書を発行いたします。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 flex items-center justify-center bg-navy rounded-full flex-shrink-0 mt-1">
                      <i className="ri-check-line text-xl text-white"></i>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg sm:text-xl font-bold text-navy mb-2">
                        トレーサビリティ
                      </h4>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        製造ロットごとに記録を保管し、品質の追跡が可能です。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* サービス詳細 */}
        <section className="py-16 sm:py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-12 sm:mb-16 lg:mb-20">
              サービス一覧
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 sm:p-8 lg:p-10 shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center bg-navy/10 rounded-full mb-6 sm:mb-8">
                    <i className={`${service.icon} text-4xl sm:text-5xl text-navy`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-navy mb-4 sm:mb-6 tracking-tight whitespace-nowrap">
                    {service.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-6 sm:mb-8">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 対応可能な材質 */}
        <section className="py-16 sm:py-20 lg:py-28 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-12 sm:mb-16">
              加工可能材質
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {materials.map((material, index) => (
                <div
                  key={index}
                  className="bg-white border-2 border-navy rounded-xl p-6 sm:p-8 hover:shadow-xl transition-all duration-300"
                >
                  <h3 className="text-xl sm:text-2xl font-bold text-navy mb-3 sm:mb-4">
                    {material.name}
                  </h3>
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-1">特徴</p>
                    <p className="text-sm sm:text-base text-gray-700">{material.features}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">用途</p>
                    <p className="text-sm sm:text-base text-gray-700">{material.applications}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BusinessPage;